// Factory for audience-aware, dedup'd, error-tolerant remote data caches.
// Callers describe the resource (name, fetcher, initial value); the factory
// returns a zustand-backed store plus a React hook. An optional audienceKey
// drives per-audience invalidation — pass it when the resource varies by
// caller (e.g. user tier); omit to share a single cache across all callers.

import { useCallback, useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { createLogger } from '@airhead-games/shared/utilities';

const SHARED_AUDIENCE = '__shared__';

export interface CreateRemoteCacheOptions<T> {
  name: string;
  initialData: T;
  fetcher: () => Promise<T>;
  fallbackError?: string;
}

export interface RemoteCacheSnapshot<T> {
  data: T;
  isFetched: boolean;
  isFetching: boolean;
  error: string | null;
  lastFetchedAt: number | null;
  audienceKey: string | null;
}

export interface RemoteCacheHookResult<T> {
  data: T;
  loading: boolean;
  error: string | null;
  isFetched: boolean;
  refetch: () => Promise<void>;
}

export interface RemoteCacheHookOptions {
  autoFetch?: boolean;
  audienceKey?: string;
}

export interface RemoteCache<T> {
  useCache: (options?: RemoteCacheHookOptions) => RemoteCacheHookResult<T>;
  fetch: (options?: { force?: boolean; audienceKey?: string }) => Promise<void>;
  reset: () => void;
  getState: () => RemoteCacheSnapshot<T>;
}

interface RemoteCacheStore<T> extends RemoteCacheSnapshot<T> {
  setData: (data: T, audienceKey: string) => void;
  patch: (next: Partial<RemoteCacheSnapshot<T>>) => void;
  reset: () => void;
}

export function createRemoteCache<T>(
  options: CreateRemoteCacheOptions<T>
): RemoteCache<T> {
  const { name, initialData, fetcher, fallbackError } = options;
  const logger = createLogger(`cache:${name}`);
  const defaultError = fallbackError ?? `Failed to load ${name}.`;

  const initialSnapshot: RemoteCacheSnapshot<T> = {
    data: initialData,
    isFetched: false,
    isFetching: false,
    error: null,
    lastFetchedAt: null,
    audienceKey: null,
  };

  const useStore = create<RemoteCacheStore<T>>((set) => ({
    ...initialSnapshot,
    setData: (data, audienceKey) =>
      set({
        data,
        audienceKey,
        isFetched: true,
        error: null,
        lastFetchedAt: Date.now(),
      }),
    patch: (next) => set(next as Partial<RemoteCacheStore<T>>),
    reset: () => set(initialSnapshot),
  }));

  let inFlight: Promise<void> | null = null;

  async function doFetch(force: boolean, requestedKey: string): Promise<void> {
    const isFresh = () => {
      const s = useStore.getState();
      return !force && s.isFetched && s.audienceKey === requestedKey;
    };

    if (isFresh()) return;

    while (inFlight) {
      await inFlight;
      if (isFresh()) return;
    }

    if (isFresh()) return;

    useStore.getState().patch({ isFetching: true, error: null });

    inFlight = (async () => {
      try {
        const data = await fetcher();
        useStore.getState().setData(data, requestedKey);
      } catch (err) {
        logger.error(`Fetch failed`, err);
        // Preserve any previously-loaded data so a transient failure doesn't
        // wipe the UI; only surface an error when we have nothing to show.
        const hasData = useStore.getState().isFetched;
        useStore.getState().patch({
          error: hasData ? null : defaultError,
        });
      } finally {
        useStore.getState().patch({ isFetching: false });
        inFlight = null;
      }
    })();

    await inFlight;
  }

  function useCache(
    hookOptions: RemoteCacheHookOptions = {}
  ): RemoteCacheHookResult<T> {
    const { autoFetch = true, audienceKey = SHARED_AUDIENCE } = hookOptions;

    const data = useStore((s) => s.data);
    const storedAudienceKey = useStore((s) => s.audienceKey);
    const isFetched = useStore((s) => s.isFetched);
    const isFetching = useStore((s) => s.isFetching);
    const error = useStore((s) => s.error);

    const stale =
      isFetched &&
      storedAudienceKey !== null &&
      storedAudienceKey !== audienceKey;

    useEffect(() => {
      if (!autoFetch) return;
      void doFetch(false, audienceKey);
    }, [autoFetch, audienceKey]);

    const refetch = useCallback(async () => {
      await doFetch(true, audienceKey);
    }, [audienceKey]);

    return useMemo(
      () => ({
        data,
        loading: isFetching || stale,
        error,
        isFetched: isFetched && !stale,
        refetch,
      }),
      [data, isFetching, stale, error, isFetched, refetch]
    );
  }

  return {
    useCache,
    fetch: ({ force = false, audienceKey = SHARED_AUDIENCE } = {}) =>
      doFetch(force, audienceKey),
    reset: () => useStore.getState().reset(),
    getState: () => useStore.getState(),
  };
}
